import { View, Pressable } from "react-native"
import { Alert } from "./ui/confirm"
import Icon from "@react-native-vector-icons/material-design-icons"
import { useForm, Controller, useFieldArray } from "react-hook-form"
import { router } from "expo-router"
import { zodResolver } from "@hookform/resolvers/zod"

import { createSupplierInput } from "@white-labels/input"
import { sharedStyles, trpc } from "@src/utils"
import { ar } from "i18n/ar"

import { BrandPicker } from "./brand-picker"
import { Button, Text, TextInput } from "./core"
import { KeyboardAwareScrollView } from "./keyboard-aware-scroll-view"

export function SupplierForm({ id }: { id?: number }) {
  const isEditing = !!id
  const utils = trpc.useUtils()
  const supplier = trpc.suppliers.get.useQuery({ id: id as number }, { enabled: isEditing })
  const createSupplier = trpc.suppliers.create.useMutation()
  const updateSupplier = trpc.suppliers.update.useMutation()

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(createSupplierInput),
    values: {
      name: supplier.data?.name ?? "",
      phones: supplier.data?.phones?.length ? supplier.data.phones.map((phone) => ({ phone })) : [{ phone: "" }],
      notes: supplier.data?.notes ?? "",
      brandIds: supplier.data?.brands?.map((brand) => brand.id) ?? [],
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: "phones" })

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      if (isEditing) {
        await updateSupplier.mutateAsync({ id: id as number, ...data })
        utils.suppliers.get.invalidate({ id: id as number })
      } else {
        await createSupplier.mutateAsync(data)
      }

      utils.suppliers.list.invalidate()
      router.back()
    } catch (error) {
      // @ts-expect-error
      Alert.alert("Error", error.message)
    }
  })

  const { errors, isValid } = formState
  const loading = createSupplier.status === "pending" || updateSupplier.status === "pending"
  const disabled = !isValid || loading

  return (
    <KeyboardAwareScrollView className="flex-1 bg-gray-50" contentContainerStyle={sharedStyles.contentContainer}>
      <Text className="text-base font-semibold text-gray-800 mb-2 text-left">{ar.name}</Text>
      <Controller
        control={control}
        name="name"
        render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} className="mb-4" placeholder={ar.enterSupplierName} autoFocus={!isEditing} />}
      />
      {errors.name && <Text className="text-red-500 mb-2">{errors.name.message}</Text>}

      <View className="mb-6">
        <Text className="text-base font-semibold text-gray-800 mb-2 text-left">{ar.phoneNumbers}</Text>
        {fields.map((field, index) => (
          <View key={field.id} className="mb-3 flex-row items-center gap-2">
            <View className="flex-1">
              <Controller
                control={control}
                name={`phones.${index}.phone`}
                render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} placeholder={ar.enterPhoneNumber} keyboardType="phone-pad" />}
              />
            </View>
            <Pressable onPress={() => remove(index)} className="p-2 rounded bg-red-100">
              <Icon name="delete" size={20} color="#EF4444" />
            </Pressable>
          </View>
        ))}
        <Pressable onPress={() => append({ phone: "" })} className="flex-row items-center justify-center bg-blue-100 border-2 border-blue-300 rounded py-2 mt-2">
          <Icon name="plus" size={20} color="#3B82F6" />
          <Text className="text-blue-600 ml-2 font-medium text-left">{ar.addPhoneNumber}</Text>
        </Pressable>
      </View>

      <View className="mb-6">
        <Text className="text-base font-semibold text-gray-800 mb-2 text-left">{ar.brands}</Text>
        <Controller control={control} name="brandIds" render={({ field }) => <BrandPicker value={field.value ?? []} onSelectionChange={field.onChange} />} />
      </View>

      <View className="mb-6">
        <Text className="text-base font-semibold text-gray-800 mb-2 text-left">{ar.notes}</Text>
        <Controller
          control={control}
          name="notes"
          render={({ field }) => (
            <TextInput
              style={{ minHeight: 120 }}
              value={field.value}
              onChangeText={field.onChange}
              placeholder={ar.enterNotes}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          )}
        />
      </View>

      <Button loading={loading} onPress={handleFormSubmit} disabled={disabled}>
        {isEditing ? ar.update : ar.save}
      </Button>
    </KeyboardAwareScrollView>
  )
}
